import React from 'react';
import { Field, change, type WrappedFieldProps } from 'redux-form';
import { ConfigProvider, Flex, Popconfirm, Segmented, theme as antdTheme } from 'antd';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import DesignSystemEditor, { designDefinitionError } from 'admin/components/common/DesignSystemEditor';
import { antdThemeDark } from 'admin/components/common/Theme';
import BrandKitSelect from 'admin/components/common/BrandKitSelect';
import DashboardButton from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import Tooltip from 'admin/components/common/Tooltip';
import t from 'utils/translate';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import {
	AUTO_STORY_DESIGN_MODES,
	areDesignDefinitionsEqual,
	copyDesignDefinition,
	createDesignDefinition,
	DEFAULT_STORY_DESIGN_MODES,
	SYSTEM_DEFAULT_FONT_FAMILY,
} from 'common/design-tokens';
import { getBrandKitById } from 'common/brand-kit/projection';
import { SYSTEM_FONTS } from 'common/constants';
import { canonicalFontFamily } from 'admin/components/common/AI/Chat/fonts/font-resolver';
import type { SettingsTabsGeneralProps } from '../types';
import css from './Tokens.scss';

const storyTokensTheme = {
	...antdThemeDark,
	algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm],
};

function DesignField({
	input,
	meta,
	disabled,
	fonts,
	theme,
}: WrappedFieldProps & Omit<React.ComponentProps<typeof DesignSystemEditor>, 'value' | 'onChange'>) {
	return (
		<DesignSystemEditor
			value={input.value || undefined}
			onChange={input.onChange}
			disabled={disabled || meta.submitting}
			fonts={fonts}
			fontContext="story"
			theme={theme}
		/>
	);
}

export default function Tokens(props: SettingsTabsGeneralProps) {
	const { fields, version, formInfo, formValues } = props;
	const organization = useAdminSelector(selectActiveOrganization);
	const fonts = [
		{ fontFamily: SYSTEM_DEFAULT_FONT_FAMILY },
		...Object.values({
			...SYSTEM_FONTS,
			...formValues.storyVersions?.[version]?.settings.fonts,
		}).filter(Boolean),
	];
	const dispatch = useAdminDispatch();
	const selectedBrandKitId = formValues.storyVersions?.[version]?.settings.brandKitId ?? '';
	const selectedBrandKit = getBrandKitById(organization?.metadata?.brandKits, selectedBrandKitId);
	const currentDesign = formValues.storyVersions?.[version]?.settings.design ?? createDesignDefinition();
	const currentDesignModes = formValues.storyVersions?.[version]?.settings.designModes;
	const themeActivation = currentDesignModes?.activation.theme;
	const defaultThemeModeId = currentDesign.modeGroups.find(group => group.id === 'theme')?.defaultModeId ?? 'light';
	const appearance =
		themeActivation?.type === 'fixed'
			? themeActivation.modeId
			: themeActivation?.type === 'media'
				? 'auto'
				: defaultThemeModeId;
	const designMatchesBrandKit =
		!!selectedBrandKit && areDesignDefinitionsEqual(currentDesign, selectedBrandKit.design);
	const resetDisabledReason = !selectedBrandKit
		? t('designTokens.resetNoBrandKit')
		: designMatchesBrandKit && themeActivation
			? t('designTokens.resetAlreadyMatches')
			: undefined;
	const reset = () => {
		if (!selectedBrandKit) return;
		dispatch(change(formInfo.name, fields.design.name(version), copyDesignDefinition(selectedBrandKit.design)));
		if (!themeActivation) {
			// Reset is an explicit adoption of a mode-aware Brand Kit. Initialize only the missing Theme
			// activation so existing policies for Theme or future mode groups remain Story-owned.
			dispatch(
				change(formInfo.name, fields.designModes.name(version), {
					activation: {
						...(currentDesignModes?.activation ?? {}),
						theme: DEFAULT_STORY_DESIGN_MODES.activation.theme,
					},
				})
			);
		}
		const defaultFontFamily = selectedBrandKit.design.defaults.root.fontFamily.base;
		const organizationFonts = Object.entries(organization?.metadata?.fonts ?? {}).filter(
			([, font]) => canonicalFontFamily(font.fontFamily) === canonicalFontFamily(defaultFontFamily)
		);
		if (organizationFonts.length) {
			dispatch(
				change(formInfo.name, fields.fonts.name(version), {
					...(formValues.storyVersions?.[version]?.settings.fonts ?? {}),
					...Object.fromEntries(organizationFonts),
				})
			);
		}
	};
	const changeBrandKit = (brandKitId: string) => {
		dispatch(change(formInfo.name, fields.brandKitId.name(version), brandKitId || null));
	};
	const changeAppearance = (next: string | number) => {
		const modeId = String(next);
		dispatch(
			change(
				formInfo.name,
				fields.designModes.name(version),
				modeId === 'auto'
					? AUTO_STORY_DESIGN_MODES
					: {
							activation: {
								...(currentDesignModes?.activation ?? {}),
								theme: { type: 'fixed', modeId },
							},
						}
			)
		);
	};
	const resetButton = (
		<Popconfirm
			disabled={!!resetDisabledReason || formInfo.submitting}
			title={t('designTokens.resetConfirm')}
			onConfirm={reset}
		>
			<DashboardButton
				theme="dark"
				view="secondary-danger"
				color="danger"
				size="medium"
				disabled={formInfo.submitting || !!resetDisabledReason}
			>
				{t('designTokens.reset')}
			</DashboardButton>
		</Popconfirm>
	);
	return (
		<ConfigProvider theme={storyTokensTheme}>
			<div className={css.tokens}>
				<header className={css.heading}>
					<Text
						tag="h1"
						className={css.title}
						size={Text.size.subheading}
						weight={Text.weight.semibold}
						compact
					>
						{t('designTokens.title')}
					</Text>
					<Text
						className={css.description}
						size={Text.size.body}
						text="Default and reusable style values shared by every card in this story. Changes here don't affect the Brand Kit."
						compact
					/>
				</header>
				<section className={css.appearance} aria-labelledby="story-appearance-heading">
					<div className={css.appearanceCopy}>
						<Text
							tag="h2"
							id="story-appearance-heading"
							size={Text.size.label}
							weight={Text.weight.semibold}
							compact
						>
							Story appearance
						</Text>
						<Text
							id="story-appearance-description"
							className={css.description}
							size={Text.size.label}
							compact
						>
							{appearance === 'auto'
								? "Follows the viewer's system color-scheme preference."
								: `This Story uses ${appearance === 'dark' ? 'Dark' : 'Light'} appearance.`}
						</Text>
					</div>
					<Segmented
						className={css.appearanceControl}
						aria-label="Story appearance"
						aria-describedby="story-appearance-description"
						value={appearance}
						disabled={formInfo.submitting}
						options={[
							{ value: 'auto', label: 'Auto' },
							{ value: 'light', label: 'Light' },
							{ value: 'dark', label: 'Dark' },
						]}
						onChange={changeAppearance}
					/>
				</section>
				<Field
					name={fields.design.name(version)}
					component={DesignField}
					fonts={fonts}
					theme="dark"
					validate={designDefinitionError}
				/>
				<Flex vertical gap="large" aria-labelledby="story-token-brand-kit-heading" style={{ marginBlock: 40 }}>
					<Flex vertical gap="large">
						<Text tag="h2" id="story-token-brand-kit-heading" size={Text.size.article}>
							Brand Kit
						</Text>
						<Text className={css.brandKitDescription} size={Text.size.label}>
							• The selected Brand Kit supplies assets, links, and guidelines to AI.
							<br />• Reset replaces this Story&apos;s design modes, defaults, tokens, and semantic assets
							with this kit&apos;s design.
						</Text>
					</Flex>
					<div className={css.brandKitRow}>
						<BrandKitSelect
							className={css.brandKitSelect}
							id="story-token-brand-kit"
							ariaLabel="Brand Kit context"
							theme="dark"
							kits={organization?.metadata?.brandKits ?? []}
							value={selectedBrandKitId}
							includeNoBrandKit
							onChange={changeBrandKit}
							menuPlacement="top"
						/>
						{resetDisabledReason ? (
							<Tooltip isStatic stylePreset="dark-2" placement="top" content={resetDisabledReason}>
								{resetButton}
							</Tooltip>
						) : (
							resetButton
						)}
					</div>
				</Flex>
			</div>
		</ConfigProvider>
	);
}
